/* eslint-disable jsx-a11y/no-autofocus */

import React, { useState, useEffect } from 'react'
import { Modal, Header, Radio } from 'semantic-ui-react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { navigate } from 'gatsby'
// Components
import {
  Primary as SubmitButton,
  Basic as CancelButton,
  StyledInput,
} from '../'
import editIcon from '../../svg/Edit Icon.svg'
import infoIcon from '../../svg/InfoIcon.svg'
import EnterEnrollerId from './EnterEnrollerId'
import VerifyDeleteFirebaseUser from './VerifyDeleteFirebaseUser'
// Context
import { usePrismic } from '../../context/PrismicContext'
import { useAuthContext } from '../../context/AuthContext'
// Hooks
import useShowError from '../../hooks/useShowError'
// Styles
import {
  Container,
  ErrorMsg,
  FieldWrapper,
  Logo,
  StyledForm,
  StyledModal,
  StyledText,
  StyledTextBold,
  StyledImage,
  ThemedPopup,
} from './Verification.styles'
import { StyledCheckboxLabel } from '../../styles/RegisterPage.styles'
// Utils
import { existingCustomer, newCustomer } from '../../utils/validations'
import { addPotentialEnrollment } from '../../utils/enrollmentHelpers'

const ExistingQ = ({ isOpen, getUserCallback }) => {
  const {
    prismicData: {
      prismicExistingQModal: {
        cancel: cancelText,
        continue: continueText,
        first_name,
        last_name,
        logo,
        no: noText,
        placeholder,
        yes: yesText,
      },
    },
  } = usePrismic()

  const [open, setOpen] = useState(isOpen)
  const [isLoading, setIsLoading] = useState(false)

  const [isEnterEnrollerIdOpen, setIsEnterEnrollerIdOpen] = useState(false)
  const [enrollerId, setEnrollerId] = useState('')
  const [areYouAReferral, setAreYouAReferral] = useState(true)
  const [isPopupOpen, setIsPopupOpen] = useState(false)

  const [isVerifyCancelModalOpen, setIsVerifyCancelModalOpen] = useState(false)
  const [isDeleteFirebaseUserLoading, setIsDeleteFirebaseUserLoading] =
    useState(false)

  const {
    handleAddNewUser,
    manageReferral: { referralData, isReferral },
    getCurrentFirebaseUserEmail,
    deleteCurrentFirebaseUser,
  } = useAuthContext()

  const [error, setError, showError] = useShowError()
  const [isNewSignUp, setIsNewSignUp] = useState(false)

  const getIsNewSignUpFromStorage = () => {
    const value = localStorage.getItem('isNewSignUp')
    if (value === null) return setIsNewSignUp(false)
    setIsNewSignUp(value === 'true' ? true : false)
  }

  useEffect(() => {
    getIsNewSignUpFromStorage()
  }, [])

  const handleSetResolvers = () => ({
    resolver: !isNewSignUp
      ? yupResolver(existingCustomer)
      : yupResolver(newCustomer),
    defaultValues: {
      firstName: referralData?.firstname ?? '',
      lastName: referralData?.lastname ?? '',
    },
  })

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm(handleSetResolvers())

  const resetAndShowError = message => {
    reset()
    setError(message)
  }

  const onSubmit = data => {
    getUserCallback(data.customer, resetAndShowError)
  }

  const handleClose = () => {
    setIsVerifyCancelModalOpen(true)
  }

  const handleVerifyDeleteFirebaseUser = async () => {
    setIsDeleteFirebaseUserLoading(true)
    await deleteCurrentFirebaseUser()
    setIsVerifyCancelModalOpen(false)
    setOpen(false)
    setIsDeleteFirebaseUserLoading(false)
    navigate('/')
  }

  const handleIsNewUser = async userInfo => {
    setIsLoading(true)
    const userInfoWithEnrollerId = {
      ...userInfo,
      enrollerLegacyAssociateId: enrollerId ? parseInt(enrollerId) : null,
    }
    await handleAddNewUser(userInfoWithEnrollerId).then(() =>
      setIsLoading(false)
    )
  }

  // handle enroller data
  const handleOnSaveEnrollerId = value => {
    if (isNaN(value)) return
    setEnrollerId(value)
    setIsEnterEnrollerIdOpen(false)
  }

  const handleCloseEnrollerId = (parentValue, childValue) => {
    if (parentValue === childValue) return setIsEnterEnrollerIdOpen(false)
    setEnrollerId(parentValue)
    return setIsEnterEnrollerIdOpen(false)
  }

  useEffect(() => {
    if (areYouAReferral) return
    setEnrollerId('')
  }, [areYouAReferral])

  const firebaseEmail = getCurrentFirebaseUserEmail()
  useEffect(() => {
    if (!firebaseEmail) return
    addPotentialEnrollment({
      email: firebaseEmail || '',
      firstName: referralData?.firstname || '',
      lastName: referralData?.lastname || '',
      slug: referralData?.store || '',
      associateType: referralData?.type || '',
      ambassadorName: referralData?.ambassadorName || '',
      isReferral,
    })
  }, [firebaseEmail])

  // TODO - Add loading state to button for non-existing customer
  return (
    <StyledModal
      closeIcon
      centered={false}
      closeOnDimmerClick={false}
      closeOnEscape={false}
      onClose={handleClose}
      open={open}
    >
      <Container direction="column">
        <Logo src={logo.url} />
        {!!isNewSignUp ? (
          <>
            <StyledForm onSubmit={handleSubmit(handleIsNewUser)}>
              <FieldWrapper>
                <ErrorMsg>{errors.firstName?.message}</ErrorMsg>
                <StyledInput
                  {...register('firstName')}
                  placeholder={first_name[0].text}
                  onFocus={() => setError('')}
                  autoFocus
                  margin="0"
                />
              </FieldWrapper>

              <FieldWrapper>
                <ErrorMsg>{errors.lastName?.message}</ErrorMsg>
                <StyledInput
                  {...register('lastName')}
                  placeholder={last_name[0].text}
                  onFocus={() => setError('')}
                  margin="0.5rem 0 1rem 0"
                />
              </FieldWrapper>

              {referralData?.store.length > 0 ? (
                <Container direction="column">
                  <StyledText>referred by:</StyledText>
                  <Container padding="0">
                    {enrollerId.length > 0 && !isNaN(enrollerId) ? (
                      <StyledTextBold>{`Referrer's ID Number: ${enrollerId}`}</StyledTextBold>
                    ) : (
                      <StyledTextBold>
                        {referralData?.ambassadorName ?? ''}
                      </StyledTextBold>
                    )}
                    <StyledImage
                      src={editIcon}
                      onClick={() => setIsEnterEnrollerIdOpen(true)}
                      style={{ marginLeft: 12 }}
                    />
                  </Container>
                </Container>
              ) : (
                <Container
                  direction="column"
                  maxWidth="300px"
                  padding="16px 0 16px 0"
                >
                  <StyledTextBold>
                    Were you referred to Q Sciences?
                  </StyledTextBold>
                  <Container justify="space-between" padding="8px 0">
                    <Radio
                      checked={areYouAReferral}
                      label={
                        <StyledCheckboxLabel>
                          {yesText[0].text}
                        </StyledCheckboxLabel>
                      }
                      onChange={() => setAreYouAReferral(true)}
                    />
                    <StyledInput
                      style={{ flex: 1, margin: '0 8px' }}
                      placeholder={`Referrer's ID Number`}
                      onFocus={() => setAreYouAReferral(true)}
                      value={enrollerId}
                      onChange={e => setEnrollerId(e.target.value)}
                    />
                    <ThemedPopup
                      content="If you don't know the ID number of the Ambassador that referred you, contact them and ask."
                      on="click"
                      onClose={() => setIsPopupOpen(false)}
                      onOpen={() => setIsPopupOpen(true)}
                      open={isPopupOpen}
                      position="bottom right"
                      hideOnScroll={true}
                      trigger={<StyledImage src={infoIcon} />}
                    />
                  </Container>
                  <Container
                    padding="none"
                    justify="flex-start"
                    onClick={() => setAreYouAReferral(false)}
                  >
                    <Radio
                      checked={!areYouAReferral}
                      label={
                        <StyledCheckboxLabel>
                          {noText[0].text}
                        </StyledCheckboxLabel>
                      }
                      onChange={() => setAreYouAReferral(false)}
                      style={{ marginRight: 4, marginBottom: 0 }}
                    />
                    <StyledText style={{ marginLeftt: 4, marginBottom: 2 }}>
                      - I found Q Sciences all by myself
                    </StyledText>
                  </Container>
                </Container>
              )}

              <SubmitButton
                disabled={
                  isLoading ||
                  isDeleteFirebaseUserLoading ||
                  (areYouAReferral && !enrollerId && !referralData?.store)
                }
                loading={isLoading}
              >
                {continueText[0].text}
              </SubmitButton>
            </StyledForm>
          </>
        ) : null}

        {error && showError()}
        <Container direction="column" padding="0">
          {!isNewSignUp ? (
            <>
              <StyledForm onSubmit={handleSubmit(onSubmit)}>
                <ErrorMsg>{errors.customer?.message}</ErrorMsg>
                <StyledInput
                  {...register('customer')}
                  placeholder={placeholder[0].text}
                  onFocus={() => setError('')}
                  autoFocus
                />
                <SubmitButton type="submit">
                  {continueText[0].text}
                </SubmitButton>
              </StyledForm>
            </>
          ) : null}
          {/* <CancelButton style={{ marginTop: '0' }} onClick={handleCancel}>
            {cancelText[0].text}
          </CancelButton> */}
        </Container>
      </Container>
      <EnterEnrollerId
        open={isEnterEnrollerIdOpen}
        onClose={handleCloseEnrollerId}
        value={enrollerId}
        onChange={e => setEnrollerId(e.target.value)}
        onSave={handleOnSaveEnrollerId}
      />
      <VerifyDeleteFirebaseUser
        open={isVerifyCancelModalOpen}
        onClose={() => {
          setIsVerifyCancelModalOpen(false)
        }}
        yesOnClick={handleVerifyDeleteFirebaseUser}
      />
    </StyledModal>
  )
}

export default ExistingQ
